import { useState } from "react";
import {
  CheckIcon,
  ChildrenIcon,
  AlertIcon,
  DashButton,
  DataSurface,
  InfoIcon,
  Note,
  PageHead,
  Panel,
  SectionHead,
  StatCard,
  StatGrid,
  StatusPill,
  TableCard,
  Td,
  Thead,
  Tr,
} from "../../../shared/components";
import { useResource } from "../../../shared/hooks/useResource";
import {
  applyOurCoupon,
  formatPaise,
  startCheckout,
  getOurSubscription,
  type OurSubscription,
} from "../../../shared/api/billing";
import type { StaffAuth } from "../../../shared/api/teaching";
import { NoPlanYet } from "./NoPlanYet";
import type { Lang } from "../../../shared/i18n";
import {
  BILLING_COPY,
  actionLabel,
  paymentStatusLabel,
  periodLabel,
  statusLabel,
} from "../superadmin/billingCopy";
/*
 * ⚠️ ACROSS THE FOLDER BOUNDARY, FOR `billingCopy`'S REASON AND WITH THE SAME
 * TRADE. The arithmetic that turns a payment ledger into a monthly series is not
 * tier-specific — it is the same question asked of one school instead of all of
 * them — and two copies of it is how a school's own total ends up disagreeing
 * with the operator's figure for that school.
 */
import {
  collectedEver,
  monthLabel,
  monthlyRevenue,
  seriesArea,
  seriesPath,
} from "../superadmin/revenueModel";

/**
 * The school's own read of F3, and the only part of it a school sees.
 *
 * 🔴 READ-ONLY, AND THE SERVER AGREES. Every write is `@Roles('SUPER_ADMIN')`:
 * a school changing its own plan is the same discount-waiting-to-happen that
 * `admin-panel.md` §4 already rules out for self-reported seats. This screen
 * draws no control at all rather than a disabled one — D4's rule.
 *
 * ⚠️ IT REUSES THE OPERATOR TIER'S DICTIONARY. The words are the same words, and
 * two copies of "past due" is how one screen ends up saying something the other
 * does not. `billingCopy.ts` lives in the superadmin folder because that is
 * where F3 is owned; importing across is honest about which tier owns the
 * vocabulary.
 */
export function OurPlanScreen({
  lang = "en",
  auth,
}: {
  lang?: Lang;
  auth?: StaffAuth;
}) {
  const t = BILLING_COPY[lang];

  const resource = useResource<OurSubscription>(
    (signal) => getOurSubscription({ ...auth, signal }),
    ["our-subscription"],
  );

  return (
    <section className="font-dash text-dash-sm text-ink">
      <PageHead title={t.changePlan} subtitle={t.seatsRule} />

      <DataSurface
        state={resource.state}
        skeletonRows={4}
        errorMessage={t.loadFailed}
        errorLastWorked={
          resource.lastLoadedAt
            ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString())
            : t.neverWorked
        }
        errorRetryLabel={t.retry}
        onRetry={resource.retry}
      >
        {resource.data === null ? null : resource.data.plan === null ? (
          /*
           * 🔴 IT SAID "No subscription" AND STOPPED — AN OPERATOR'S STATUS WORD
           * ON A SCHOOL'S OWN SCREEN. `stateNone` comes from
           * `superadmin/billingCopy.ts`, where it is one cell in a table of
           * hundreds of schools and the reader is the person who fixes it. Shown
           * to the school itself it names a problem, offers nothing, and leaves
           * a page that is otherwise blank. §"Every data surface ships four
           * states" is explicit: an empty state is *"a sentence plus one action
           * — never blank white space"*.
           */
          <NoPlanYet
            children0={resource.data.places.used}
            lang={lang}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                label={t.plan}
                value={resource.data.plan.name}
                icon={<CheckIcon size={18} />}
                tone="primary"
              />
              {/* 🔴 THE SEAT COUNT, WITH THE RULE ABOVE IT IN THE SUBTITLE. A
                  school reading a charge must be able to see what produced it. */}
              <StatCard
                label={t.seats}
                value={resource.data.seats}
                icon={<ChildrenIcon size={18} />}
                tone="indigo"
              />
              {/*
                🔴 THE TILE SHOWS WHAT THEY ACTUALLY PAY, NOT THE LIST PRICE.
                A school on a discount shown the undiscounted figure here would be
                reading a number that disagrees with its own invoice — and
                `design-system.md` §8's rule is that sibling panels cannot
                contradict each other. What the discount was OFF is drawn below,
                where there is room to say both.
              */}
              <StatCard
                label={t.charge}
                value={
                  resource.data.netChargePaise === null
                    ? "—"
                    : formatPaise(
                        resource.data.netChargePaise,
                        resource.data.plan.currency,
                      )
                }
                icon={<InfoIcon size={18} />}
                tone="teal"
              />
              {/*
                🔴 A TRIAL ENDS; IT DOES NOT RENEW. The same date under the same
                heading for a school that is paying and one that is not is the
                single most confusing thing this screen could do — a school on a
                free trial reading "Renews" concludes they are being billed.
              */}
              <StatCard
                label={
                  resource.data.status === "TRIAL" ? t.trialEnds : t.renews
                }
                value={
                  resource.data.status === "TRIAL"
                    ? resource.data.trialEndsAt === null
                      ? "—"
                      : new Date(resource.data.trialEndsAt).toLocaleDateString()
                    : resource.data.currentPeriodEnd === null
                      ? "—"
                      : new Date(
                          resource.data.currentPeriodEnd,
                        ).toLocaleDateString()
                }
                icon={<CheckIcon size={18} />}
                tone="leaf"
              />
            </StatGrid>

            <div className="mt-4">
              <Panel>
                <p className="flex flex-wrap items-center gap-3">
                  <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
                    {statusLabel(resource.data.status, lang)}
                  </StatusPill>
                  {resource.data.paidUntil !== null && (
                    <span className="text-dash-sm text-muted">
                      {t.paidUntil}:{" "}
                      <span className="font-bold text-ink">
                        {new Date(resource.data.paidUntil).toLocaleDateString()}
                      </span>
                    </span>
                  )}
                </p>

                {/*
                  🔴 THE DISCOUNT, WITH WHAT IT CAME OFF. `discount.ts` makes the
                  argument: a net price with no gross beside it is a number a
                  school cannot check, and this is the screen their finance office
                  reads an invoice against.
                */}
                {resource.data.couponCode !== null &&
                  resource.data.discountPaise !== null && (
                    <p className="mt-3 flex flex-wrap items-center gap-3 text-dash-xs text-muted">
                      <StatusPill tone="success" icon={<CheckIcon size={14} />}>
                        {t.discountOff(
                          resource.data.couponOff ?? "",
                          resource.data.couponCode,
                        )}
                      </StatusPill>
                      <span>
                        {t.listPrice}:{" "}
                        <span className="tabular font-bold text-ink line-through">
                          {formatPaise(
                            resource.data.chargePaise ?? 0,
                            resource.data.plan.currency,
                          )}
                        </span>
                      </span>
                      <span>
                        {t.saved}:{" "}
                        <span className="tabular font-bold text-ink">
                          {formatPaise(
                            resource.data.discountPaise,
                            resource.data.plan.currency,
                          )}
                        </span>
                      </span>
                      {/* ⚠️ HOW LONG IT LASTS, BECAUSE THE INVOICE GOING BACK UP IS
                        the thing a school will otherwise ring about. */}
                      <span>
                        {resource.data.couponPeriodsLeft === null
                          ? t.couponForever
                          : t.couponPeriodsLeft(
                              resource.data.couponPeriodsLeft,
                            )}
                      </span>
                    </p>
                  )}

                {/*
                  🔴 A CODE FIELD IS NOT A PRICE CONTROL, AND THAT IS WHY IT MAY
                  EXIST ON A READ-ONLY SCREEN. The header's rule stands: a school
                  cannot change its plan, its seats or its price. What it can do
                  is name a discount an operator already wrote down — the server
                  decides whether it applies and what it is worth, exactly as it
                  decides the charge.

                  ⚠️ EXPECT ONE GENERIC REFUSAL AND DO NOT IMPROVE ON IT. Unknown,
                  expired, used up, meant for another school and "you have already
                  paid" all come back identically, because a code is typed off an
                  email and telling them apart would say which codes are real.
                */}
                {resource.data.couponCode === null && (
                  <div className="mt-4">
                    <CouponEntry
                      lang={lang}
                      auth={auth}
                      onApplied={resource.retry}
                    />
                  </div>
                )}

                {/*
                  🔴 THE ONE CONTROL ON A SCREEN THAT HAS ALWAYS BEEN READ-ONLY,
                  AND IT DOES NOT BREAK THAT RULE. The header above still holds:
                  every write to a subscription is `@Roles('SUPER_ADMIN')`, and a
                  school still cannot change its plan, its seats or its price.
                  What it can now do is PAY the arrangement an operator put it
                  on — the amount is computed on the server and nothing in the
                  request influences it.
                */}
                <div className="mt-4">
                  <PayPanel
                    lang={lang}
                    auth={auth}
                    available={resource.data.onlinePaymentAvailable}
                    /* 🔴 THE NET, WHICH IS WHAT THE HOSTED PAGE WILL ASK FOR. A
                       school told "₹8,000" here and shown "₹6,400" at Razorpay
                       has been told the wrong number by its own supplier — and
                       the server computes both from the same breakdown, so the
                       only way they can disagree is this prop. */
                    chargePaise={resource.data.netChargePaise}
                    currency={resource.data.plan.currency}
                    period={resource.data.plan.period}
                  />
                </div>
              </Panel>
            </div>

            {/*
              🔴 WHAT THE SCHOOL HAS ACTUALLY PAID, WHICH THIS SCREEN COULD NOT
              SHOW BEFORE. The timeline below says a payment was RECORDED; it
              never said how much or by what route, because until `Payment`
              existed nothing knew. This is the table an admin is asked for when
              their own finance office queries an invoice.
            */}
            {resource.data.payments.length > 0 && (
              <>
                <SectionHead count={resource.data.payments.length}>
                  {t.paymentsTitle}
                </SectionHead>
                <TableCard>
                  <table className="w-full border-collapse">
                    <Thead
                      headers={[t.when, t.amount, t.how, t.paymentStatus]}
                    />
                    <tbody>
                      {resource.data.payments.map((p) => (
                        <Tr key={p.id}>
                          <Td soft>
                            {new Date(
                              p.paidAt ?? p.createdAt,
                            ).toLocaleDateString()}
                          </Td>
                          <Td bold>{formatPaise(p.amountPaise, p.currency)}</Td>
                          {/* ⚠️ THE RAIL, NEVER AN INSTRUMENT — "upi", "card".
                              There are no card digits anywhere in this product. */}
                          <Td soft>
                            {p.method ??
                              (p.source === "MANUAL" ? t.byHand : "—")}
                          </Td>
                          <Td soft>{paymentStatusLabel(p.status, lang)}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </table>
                </TableCard>
              </>
            )}

            {/* 🔴 CHANGE 19 §3 — *"provide schools with complete financial and
                revenue-related metrics."* The screen already listed individual
                payments; what it never answered is the question a bursar
                actually opens it with — *"what has this cost us, and what is it
                costing us per child?"* Both come out of rows already on this
                response, so this panel adds no request. */}
            <div className="mt-4">
              <SpendPanel
                payments={resource.data.payments}
                seats={resource.data.seats}
                netChargePaise={resource.data.netChargePaise}
                discountPaise={resource.data.discountPaise}
                currency={resource.data.plan.currency}
                lang={lang}
              />
            </div>

            <SectionHead count={resource.data.timeline.length}>
              {t.timeline}
            </SectionHead>
            {resource.data.timeline.length === 0 ? (
              <p className="text-muted">{t.noTimeline}</p>
            ) : (
              <TableCard>
                <table className="w-full border-collapse">
                  <Thead headers={[t.when, t.what, t.plan, t.seats]} />
                  <tbody>
                    {resource.data.timeline.map((e) => (
                      <Tr key={e.id}>
                        <Td soft>
                          {new Date(e.effectiveAt).toLocaleDateString()}
                        </Td>
                        <Td bold>{actionLabel(e.action)}</Td>
                        <Td soft>{e.planName ?? "—"}</Td>
                        <Td num>{e.seats ?? "—"}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </TableCard>
            )}
            {/* ⚠️ THE OPERATOR'S NOTES ARE NOT SHOWN HERE. "NEFT ref 8821" is an
                internal reminder; a school reading its own timeline needs what
                happened and when, not what somebody wrote to themselves. */}
          </>
        )}
      </DataSurface>
    </section>
  );
}

/**
 * A school entering a discount code.
 *
 * 🔴 IT DRAWS ONE SENTENCE FOR EVERY FAILURE AND NEVER READS THE SERVER'S. That
 * is the opposite of what `PayPanel` below does, deliberately: the checkout's
 * refusals name a next action ("this school is not on a plan yet"), while every
 * refusal here would name a fact about a code — whether it exists, whether it is
 * spent, whether it was meant for somebody else. A code is typed off an email, so
 * it is guessable, and a screen that distinguished those would be a tool for
 * finding real codes. The server already sends one sentence; this does not try to
 * be cleverer than it.
 *
 * ⚠️ AND THERE IS NO "REMOVE" HERE. Taking a discount off is operator-only: the
 * claim is already consumed, so a school that removed its own by accident could
 * not put back a code that had since run out.
 */
function CouponEntry({
  lang,
  auth,
  onApplied,
}: {
  lang: Lang;
  auth?: StaffAuth;
  onApplied: () => void;
}) {
  const t = BILLING_COPY[lang];
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        setFailed(false);
        void applyOurCoupon(code.trim(), auth)
          .then(() => {
            setCode("");
            onApplied();
          })
          .catch(() => {
            setFailed(true);
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <label className="text-dash-xs font-bold text-muted" htmlFor="our-coupon">
        {t.applyCoupon}
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          id="our-coupon"
          value={code}
          maxLength={40}
          autoComplete="off"
          /* ⚠️ Uppercased as they type, matching what the server stores — a field
             showing one spelling while the lookup uses another is a refusal
             nobody can debug. */
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          className="rounded-xl border border-line bg-surface px-3 py-2 font-dash text-dash-sm tracking-[0.08em] text-ink"
        />
        <DashButton
          type="submit"
          variant="secondary"
          disabled={busy || code.trim() === ""}
        >
          {busy ? t.applying : t.applyIt}
        </DashButton>
      </div>
      {failed && (
        <p role="alert">
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {t.couponRejected}
          </Note>
        </p>
      )}
    </form>
  );
}

/**
 * The pay control.
 *
 * 🔴 IT ASKS THE SERVER WHETHER PAYING IS POSSIBLE BEFORE DRAWING ANYTHING.
 * D4's rule: a school shown a "Pay now" button that cannot work concludes the
 * product is broken. A deployment with no Razorpay credentials is a legitimate
 * configuration — plenty of schools pay by NEFT and an operator records it — so
 * the honest screen says which of the two this is.
 *
 * 🔴 AND IT REDIRECTS RATHER THAN EMBEDDING A CHECKOUT. Razorpay's own hosted
 * page means a card number never touches this origin: there is no checkout
 * script on the page, nothing to intercept, and no PCI surface here at all. The
 * cost is a full navigation away and back, which for a payment is the right
 * trade — and it is the reason `index.css` still self-hosts every asset.
 */
function PayPanel({
  lang,
  auth,
  available,
  chargePaise,
  currency,
  period,
}: {
  lang: Lang;
  auth?: StaffAuth;
  available: boolean;
  chargePaise: number | null;
  currency: string;
  period: string;
}) {
  const t = BILLING_COPY[lang];
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  if (!available) {
    /* Not a disabled button. A sentence naming the other way to pay. */
    return (
      <Note tone="neutral" icon={<InfoIcon size={16} />}>
        {t.payUnavailable}
      </Note>
    );
  }
  if (chargePaise === null || chargePaise <= 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-dash-xs text-muted">
        {t.payFor(
          formatPaise(chargePaise, currency),
          periodLabel(period, lang),
        )}
      </p>
      <div>
        <DashButton
          variant="primary"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setFailed(null);
            void startCheckout(auth)
              .then((start) => {
                /*
                 * ⚠️ `location.assign`, NOT `window.open`. A popup is blocked by
                 * default on a click that has already awaited a request, and a
                 * blocked payment window is a school that thinks the button is
                 * broken. A full navigation always works; Razorpay sends them
                 * back here afterwards.
                 */
                window.location.assign(start.url);
              })
              .catch((err: unknown) => {
                /* The server's sentence where there is one — "amount exceeds
                   maximum", "this school is not on a plan yet" — because each
                   names a different next action. */
                setFailed(
                  err instanceof Error && err.message
                    ? err.message
                    : t.payFailed,
                );
                setBusy(false);
              });
          }}
        >
          {busy ? t.payOpening : t.payNow}
        </DashButton>
      </div>
      {/* 🔴 WHAT HAPPENS AFTER THEY PAY, SAID BEFORE THEY DO. Confirmation is a
          webhook, so the screen may not update the instant they return — and a
          school that does not know that will pay twice. */}
      <p className="text-dash-xs text-muted">{t.payConfirmHint}</p>
      {failed !== null && (
        <Note tone="warning" icon={<AlertIcon size={16} />}>
          {failed}
        </Note>
      )}
    </div>
  );
}

/**
 * Change 19 §3, school side — what this has cost, and what it costs per child.
 *
 * 🔴 IT SEPARATES WHAT WAS PAID FROM WHAT IS CHARGED, which is the one thing a
 * money panel must not blur. "Paid so far" is history and comes from the
 * payments ledger; "per child, per period" is a live figure that moves whenever
 * a child is enrolled. `revenueModel.ts` makes the same separation for the
 * operator, and this is the single-school version of it.
 *
 * ⚠️ NOTHING HERE IS A FORECAST. There is no "projected annual spend": the seat
 * count moves with enrolment and a school shown a year's projection would be
 * shown a number that is wrong the next time a class is added. What a school can
 * check — what they have paid, and what a child currently costs — is what is
 * drawn.
 */
function SpendPanel({
  payments,
  seats,
  netChargePaise,
  discountPaise,
  currency,
  lang,
}: {
  payments: OurSubscription["payments"];
  seats: number;
  netChargePaise: number | null;
  discountPaise: number | null;
  currency: string;
  lang: Lang;
}) {
  const t = BILLING_COPY[lang];
  const now = new Date();
  const series = monthlyRevenue(payments, now, 12);
  const paidEver = collectedEver(payments);
  const twelveMonths = series.reduce((sum, p) => sum + p.paise, 0);

  /*
   * 🔴 THE EFFECTIVE RATE, NOT THE PLAN'S RATE. A school on a discount pays less
   * per child than the plan says, and the plan's own per-child figure is the one
   * a bursar would otherwise multiply out and fail to reconcile.
   *
   * ⚠️ `null` WHEN THERE ARE NO SEATS — never a division by zero, and never "₹0
   * per child", which reads as free rather than as unknown.
   */
  const perChild =
    netChargePaise === null || seats <= 0 ? null : Math.round(netChargePaise / seats);

  return (
    <Panel>
      <SectionHead hint={t.spendHint}>{t.spendTitle}</SectionHead>

      <div className="mt-3 grid gap-4 sm:grid-cols-3">
        <div>
          <span className="block text-dash-xs font-bold text-muted">{t.spendPaidEver}</span>
          <span className="tabular mt-0.5 block text-dash-lg font-extrabold text-ink">
            {formatPaise(paidEver, currency)}
          </span>
        </div>
        <div>
          <span className="block text-dash-xs font-bold text-muted">{t.spendTwelveMonths}</span>
          <span className="tabular mt-0.5 block text-dash-lg font-extrabold text-ink">
            {formatPaise(twelveMonths, currency)}
          </span>
        </div>
        <div>
          <span className="block text-dash-xs font-bold text-muted">{t.spendPerChild}</span>
          <span className="tabular mt-0.5 block text-dash-lg font-extrabold text-ink">
            {perChild === null ? "—" : formatPaise(perChild, currency)}
          </span>
          <span className="mt-0.5 block text-dash-xs text-muted">
            {perChild === null ? t.spendPerChildNone : t.spendPerChildHint(seats)}
          </span>
        </div>
      </div>

      {/* 🔴 WHAT THE DISCOUNT IS WORTH, IN MONEY, WHERE THE SPENDING IS. The
          plan panel above says which coupon is attached; this says what it comes
          to at today's enrolment, which is the figure anybody asks about. */}
      {discountPaise !== null && discountPaise > 0 ? (
        <p className="mt-3 text-dash-xs font-bold text-emerald-700">
          {t.spendDiscount(formatPaise(discountPaise, currency))}
        </p>
      ) : null}

      {paidEver === 0 ? (
        <p className="mt-4 text-dash-xs text-muted">{t.spendNothingYet}</p>
      ) : (
        <div className="mt-4">
          <div className="h-28 w-full">
            <svg
              className="h-full w-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              role="img"
              aria-label={t.spendChartLabel}
            >
              <defs>
                <linearGradient id="schoolSpendGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={seriesArea(series)} fill="url(#schoolSpendGrad)" />
              <path
                d={seriesPath(series)}
                fill="none"
                stroke="var(--color-primary)"
                strokeWidth="1.2"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </div>
          <div className="mt-1 flex justify-between text-[11px] font-bold text-muted">
            {series.map((point, i) => (
              <span key={point.month}>{i % 3 === 0 ? monthLabel(point.month, now) : ""}</span>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}
